import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileDrop from "@/components/admin/FileDrop";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const EMPTY = { name: "", image: "", active: true };

export default function MediaLogosAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/media-logos/all").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.name || !f.image) return toast.error("Name and logo image are required");
    try {
      if (f.id) await api.put(`/media-logos/${f.id}`, f);
      else await api.post("/media-logos", f);
      toast.success("Saved"); setOpen(false); setF(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (l) => { if (!window.confirm("Delete this logo?")) return; await api.delete(`/media-logos/${l.id}`); toast.success("Deleted"); load(); };
  const toggle = async (l) => { await api.put(`/media-logos/${l.id}`, { active: !l.active }); load(); };
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/media-logos/reorder", { ids: arr.map((l) => l.id) }); }
    catch { toast.error("Could not save order"); load(); }
  };

  return (
    <>
      <AdminHeader title="Media Logos — As Seen In" subtitle={`${items.length} logos`} actions={
        <Button onClick={() => { setF(EMPTY); setOpen(true); }} className="bg-npst-navy text-white" data-testid="add-logo-btn"><Plus size={15} className="mr-1.5" /> Add Logo</Button>
      } />
      <AdminPage>
        <p className="text-sm text-slate-500 mb-4">Upload media / publication logos shown in the animated "As Seen In" strip on the homepage. Reorder with the arrows; deactivate to hide without deleting.</p>
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {items.length === 0 ? <p className="p-6 text-slate-400 text-sm">No logos yet.</p> : items.map((l, idx) => (
            <div key={l.id} className={`flex items-center justify-between px-4 py-3 ${l.active ? "" : "opacity-50"}`} data-testid="logo-row">
              <div className="flex items-center gap-4 min-w-0">
                <img src={l.image} alt={l.name} className="h-9 w-auto max-w-[120px] object-contain bg-npst-bgAlt rounded p-1" />
                <span className="font-medium text-npst-ink truncate">{l.name}</span>
                {!l.active && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Hidden</span>}
              </div>
              <div className="flex items-center gap-3 text-slate-400 shrink-0">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="hover:text-npst-navy disabled:opacity-30" data-testid="logo-up-btn"><ArrowUp size={15} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="hover:text-npst-navy disabled:opacity-30" data-testid="logo-down-btn"><ArrowDown size={15} /></button>
                <Switch checked={l.active} onCheckedChange={() => toggle(l)} data-testid="logo-active-toggle" />
                <button onClick={() => { setF(l); setOpen(true); }} className="text-xs font-semibold uppercase text-npst-navy hover:underline" data-testid="logo-edit-btn">Edit</button>
                <button onClick={() => del(l)} className="hover:text-red-600" data-testid="logo-delete-btn"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </AdminPage>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Edit" : "Add"} Logo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">Logo Image</label>
              <FileDrop value={f.image} onChange={(v) => set("image", v)} label="Drag & drop a logo (PNG/JPG/WEBP), or click to browse" /></div>
            <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">Media / Publication Name</label>
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Zee News" data-testid="logo-name" /></div>
            <label className="flex items-center gap-2 text-sm text-npst-inkSoft"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="logo-active" /> Active (shown on homepage)</label>
            <Button onClick={save} className="w-full bg-npst-navy text-white" data-testid="logo-save-btn">Save Logo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
